/**
 * The region's buildings, as boxes a bullet can go into — the app's half of
 * AD-018's `SceneryQuery`.
 *
 * `packages/engine` cannot hold this and must not: the placement list is a
 * `.WLD` region body fetched by the browser, and `@red-opus/baker` is Node-only
 * (`region-loader.ts`'s own note). So the engine declares the seam and this file
 * fills it, exactly the arrangement `ground-elevation.ts` has with
 * `flight/ground.ts`'s `GroundQuery`.
 *
 * ## Which placements are structures — a census, not a guess
 *
 * Every placed object that has a `.GI` entry has an axis-aligned footprint and a
 * facet table. A **building** is one whose tallest facet stands above its own
 * base; a **hill** is one whose facets are gradients at height zero. Measured
 * over all six baked regions (`scenery-structures.test.ts` re-runs it against
 * `data/baked/world/*.json`, so this cannot drift from the data):
 *
 * | region | placements | `.GI` objects | roof > 0 | roof = 0 | placed structures |
 * |---|---|---|---|---|---|
 * | LONDON | 257 | 10 | 10 | 0 | 162 |
 * | DUNKIRK | 328 | 17 | 17 | 0 | 175 |
 * | SOMME | 442 | 15 | 15 | 0 | 235 |
 * | PARIS | 467 | 18 | 15 | 3 | 191 |
 * | VERDUN | 380 | 18 | 15 | 3 | 175 |
 * | PTPLANE | 18 | 2 | 2 | 0 | 1 |
 *
 * The separation is not marginal. The largest structure half-extent in the whole
 * corpus is **114.3 m** (a DUNKIRK shed) and the smallest hill half-extent is
 * **3 901 m** — a factor of 34 — and the six roof-zero objects are exactly the
 * three hill objects PARIS and VERDUN share. (Both figures were 46.2 m and
 * 1 575 m when this was first measured; the scale-exponent fix of the same day
 * grew every `scaleExponent > 0` object by 2.4765×, which moves both sides of
 * the comparison and leaves the factor of 34 exactly where it was.) `roofM > 0` is therefore the whole
 * rule, with `MAX_STRUCTURE_HALF_EXTENT_M` kept as a belt-and-braces guard
 * against a region we have not seen filing a hill with a raised facet.
 *
 * ## The box, and why it is not rotated
 *
 * `.GI` stores `extentXM`/`extentYM` as **unrotated** half-extents and the
 * original's own `giTestObjectAt` never applies the placement's `.WLD` heading
 * (docs/formats/gi-format.md §5) — which is why `ground-elevation.ts`
 * deliberately ignores `rotationDegrees` too. Rotating the fire box would put it
 * out of agreement with the ground-collision box on the same wall, so it is not
 * rotated here either. The one place this shows is a long shed placed at 45°,
 * whose fire box is its own diagonal; that is the original's error and inheriting
 * it is cheaper than owning a different one.
 *
 * ## Cost
 *
 * Built once per region load: ~200 boxes into a uniform grid, and a query is a
 * height reject plus one to four cell lookups. The height reject does nearly all
 * the work — the tallest roof in the corpus is 172.2 m and a round spends its
 * life above that, so a sortie flown at patrol height never reaches the grid at
 * all.
 *
 * ## The pad (AD-020)
 *
 * `structureOnSegment` takes an optional `padM` that grows every box by that
 * many metres before the test, so the same index answers for a *body* as well
 * as for a point. A round passes nothing and gets the identical answer AD-018
 * always gave it; the player's aeroplane passes its own
 * `airframeCollisionRadiusM` (3.81 m for a scout, 6.86 m for a two-seater — the
 * hull AD-017 already rams other aeroplanes with) so that flying into a barn is
 * the airframe hitting it rather than the pose origin. `padM` is three orders
 * of magnitude under `CELL_M`, so widening the query's cell span by it is
 * enough to reach any structure whose padded box overlaps the query and the
 * grid's own bucketing is left alone.
 */

import type { SceneryQuery, SceneryStructure, Vec3 } from "@red-opus/engine";
import { NO_SCENERY } from "@red-opus/engine";
import type { WorldRegionBody, WorldGroundObjectBody } from "./region-loader.ts";
import { ORIGIN_ZERO, placementWorldTransform, type PlacementOrigin } from "./placement-transform.ts";

/**
 * Half-extent a structure may not exceed, metres. The corpus's largest is
 * 114.3 m and its smallest hill is 3 901 m, so anything in between is a thing we
 * have never seen and would rather not set on fire.
 *
 * Re-measured 2026-08-24 after the scale-exponent fix (`london-sea-placements.md`
 * §9): every `scaleExponent > 0` object grew 2.4765×, which moved the largest
 * building from 46.2 m to 114.3 m and left this guard 5 % above the corpus
 * instead of 2.6× above it. A belt-and-braces guard that close to real data is
 * not a guard, it is a trap waiting for the next region — so it is re-pitched to
 * sit an order of magnitude clear on both sides. The rule that actually
 * separates a building from a hill is `roofM > 0`, which the fix did not touch.
 */
export const MAX_STRUCTURE_HALF_EXTENT_M = 400;

/** Grid cell, metres. Comfortably larger than the largest footprint, so a box lands in at most four cells. */
const CELL_M = 256;

/** The `.GI` object's roof: the tallest facet it has, or 0 for a gradient-only hill. */
export function roofHeightM(ground: WorldGroundObjectBody): number {
  let roof = 0;
  for (const facet of ground.facets) if (facet.baseHeightM > roof) roof = facet.baseHeightM;
  return roof;
}

/** Whether this `.GI` object is a building rather than a hill — see the census in the header. */
export function isStructure(ground: WorldGroundObjectBody): boolean {
  return (
    roofHeightM(ground) > 0 &&
    ground.extentXM <= MAX_STRUCTURE_HALF_EXTENT_M &&
    ground.extentYM <= MAX_STRUCTURE_HALF_EXTENT_M
  );
}

/**
 * Every structure the region places, in placement order.
 *
 * `key` is the index into `region.placements` — the same identity the `/assets`
 * World viewer selects by, stable because the baked array is ordered, and an
 * integer so a `SceneryFire` costs one number to say what it is about.
 *
 * `baseHeightAt` is the sampler the meshes were draped with
 * (`fly-region.ts` hands over `ReliefGround.drapeAt`), so a fire box's floor is
 * the height the building is actually drawn standing at. Omitted, everything
 * sits on the flat datum, which is the pre-relief world.
 */
export function regionStructures(
  region: WorldRegionBody,
  origin: PlacementOrigin = ORIGIN_ZERO,
  baseHeightAt?: (x: number, z: number) => number,
): readonly SceneryStructure[] {
  const structures: SceneryStructure[] = [];
  region.placements.forEach((placement, key) => {
    const ground = region.groundObjects[String(placement.objectId)];
    if (!ground || !isStructure(ground)) return;
    const { position } = placementWorldTransform(placement, origin);
    structures.push({
      key,
      x: position.x,
      z: position.z,
      baseY: baseHeightAt ? baseHeightAt(position.x, position.z) : 0,
      halfXM: ground.extentXM,
      halfZM: ground.extentYM,
      roofM: roofHeightM(ground),
    });
  });
  return structures;
}

/**
 * Where the segment `from → to` first enters the box, as a fraction in [0, 1],
 * or `null` if it never does — the slab method, and the box counterpart of
 * `combat/collide.ts`'s `segmentSphereHit`. A segment that starts inside returns
 * 0, for the same reason that one does: the round is already in the wall.
 *
 * `padM` grows the box on all six faces first (AD-020). That is the Minkowski
 * sum of the box and a sphere of that radius read conservatively — it rounds
 * the eight corners out into cubes, which errs by at most `padM(1 − 1/√3)` on
 * a diagonal approach and never the other way. A round passes nothing and gets
 * the unpadded box it always got; an aeroplane passes its own
 * `airframeCollisionRadiusM`, so the airframe is a body rather than a point.
 *
 * A zero-length segment is a point-in-box test: every axis takes the parallel
 * branch and the answer is 0 when the point is inside all three slabs and
 * `null` when it is not. `building-strike.ts` depends on that.
 */
export function segmentBoxEntry(
  from: Vec3,
  to: Vec3,
  structure: SceneryStructure,
  padM = 0,
): number | null {
  let near = 0;
  let far = 1;
  const minima = [
    structure.x - structure.halfXM - padM,
    structure.baseY - padM,
    structure.z - structure.halfZM - padM,
  ];
  const maxima = [
    structure.x + structure.halfXM + padM,
    structure.baseY + structure.roofM + padM,
    structure.z + structure.halfZM + padM,
  ];
  const starts = [from.x, from.y, from.z];
  const deltas = [to.x - from.x, to.y - from.y, to.z - from.z];
  for (let axis = 0; axis < 3; axis += 1) {
    const start = starts[axis] ?? 0;
    const delta = deltas[axis] ?? 0;
    const low = minima[axis] ?? 0;
    const high = maxima[axis] ?? 0;
    if (Math.abs(delta) < 1e-9) {
      // Parallel to this pair of slabs: either inside them for the whole
      // segment, or it can never be.
      if (start < low || start > high) return null;
      continue;
    }
    const first = (low - start) / delta;
    const second = (high - start) / delta;
    const entry = Math.min(first, second);
    const exit = Math.max(first, second);
    if (entry > near) near = entry;
    if (exit < far) far = exit;
    if (near > far) return null;
  }
  return near;
}

function cellKey(cellX: number, cellZ: number): number {
  // A 32-bit interleave would be tidier; this is a plain pairing over the ±2 048
  // cell range a 500 km region spans, which no baked map comes close to.
  return (cellX + 4096) * 8192 + (cellZ + 4096);
}

/**
 * Builds the query. Returns `NO_SCENERY` for a region that places nothing
 * burnable, so a caller never has to special-case the empty map and the engine
 * gets the identical object every pre-AD-018 caller gets.
 */
export function createSceneryQuery(structures: readonly SceneryStructure[]): SceneryQuery {
  if (structures.length === 0) return NO_SCENERY;
  let ceilingY = 0;
  const grid = new Map<number, SceneryStructure[]>();
  for (const structure of structures) {
    const top = structure.baseY + structure.roofM;
    if (top > ceilingY) ceilingY = top;
    const minCellX = Math.floor((structure.x - structure.halfXM) / CELL_M);
    const maxCellX = Math.floor((structure.x + structure.halfXM) / CELL_M);
    const minCellZ = Math.floor((structure.z - structure.halfZM) / CELL_M);
    const maxCellZ = Math.floor((structure.z + structure.halfZM) / CELL_M);
    for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
      for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ += 1) {
        const key = cellKey(cellX, cellZ);
        const bucket = grid.get(key);
        if (bucket) bucket.push(structure);
        else grid.set(key, [structure]);
      }
    }
  }

  return {
    structureOnSegment(from: Vec3, to: Vec3, padM = 0): SceneryStructure | null {
      // The reject that does nearly all the work: the tallest roof in the corpus
      // is 172.2 m and most regions top out at 34, so anything above the
      // region's own ceiling — a round, or an aeroplane at patrol height — is
      // done in two compares and never touches the grid. The pad is added to
      // the ceiling because a padded box is that much taller.
      const roof = ceilingY + padM;
      if (from.y > roof && to.y > roof) return null;
      // The cell span is widened by the pad for the same reason: a padded box
      // can reach into a cell its footprint does not.
      const minCellX = Math.floor((Math.min(from.x, to.x) - padM) / CELL_M);
      const maxCellX = Math.floor((Math.max(from.x, to.x) + padM) / CELL_M);
      const minCellZ = Math.floor((Math.min(from.z, to.z) - padM) / CELL_M);
      const maxCellZ = Math.floor((Math.max(from.z, to.z) + padM) / CELL_M);
      let nearest: SceneryStructure | null = null;
      let nearestEntry = Number.POSITIVE_INFINITY;
      for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
        for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ += 1) {
          const bucket = grid.get(cellKey(cellX, cellZ));
          if (!bucket) continue;
          for (const structure of bucket) {
            const entry = segmentBoxEntry(from, to, structure, padM);
            // Ties break on the lower `key`, so a round arriving exactly on the
            // seam between two abutting sheds always lights the same one — a
            // structure can sit in two cells and be tested twice, and the answer
            // must not depend on which cell got there first.
            if (entry === null) continue;
            if (entry < nearestEntry || (entry === nearestEntry && nearest !== null && structure.key < nearest.key)) {
              nearest = structure;
              nearestEntry = entry;
            }
          }
        }
      }
      return nearest;
    },
  };
}

/** The whole build, for a caller that has a region body and wants the query. */
export function sceneryQueryFor(
  region: WorldRegionBody,
  origin: PlacementOrigin = ORIGIN_ZERO,
  baseHeightAt?: (x: number, z: number) => number,
): SceneryQuery {
  return createSceneryQuery(regionStructures(region, origin, baseHeightAt));
}
